import React, { Dispatch, ReactElement, SetStateAction, useRef, useState } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Button,
  CModal,
  CModalBody,
  ModalCloseButton,
  ModalContent,
  CModalFooter,
  CModalHeader,
  ModalOverlay,
} from '@televet/kibble-ui';

interface ICropImageModalProps {
  fileUrl: string;
  onApplyCrop: Function; // eslint-disable-line @typescript-eslint/ban-types
  isOpen: boolean;
  onClose: () => void;
  setImageData: Dispatch<SetStateAction<ImageData | undefined>>;
}
const ModalButton = ({
  onClickHandler,
  buttonText,
}: {
  onClickHandler: () => void;
  buttonText: string;
}): JSX.Element => {
  return (
    <Button height="25px" size="xs" variant="secondary" cursor="pointer" onClick={onClickHandler}>
      {buttonText}
    </Button>
  );
};

const CropImageModal = ({
  fileUrl,
  onApplyCrop,
  isOpen,
  onClose,
  setImageData,
}: ICropImageModalProps): ReactElement => {
  const [crop, setCrop] = useState<Crop>({ aspect: 1, unit: '%', width: 50, x: 25, y: 25 });
  const imageRef = useRef<HTMLImageElement>();

  const getCroppedImg = (image: HTMLImageElement, crop: Crop): Promise<Blob> | undefined => {
    const x = crop?.x || 0;
    const y = crop?.y || 0;
    if (crop.height && crop.width && image) {
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = crop.width;
      canvas.height = crop.height;
      const ctx = canvas.getContext('2d');

      ctx?.drawImage(
        image,
        x * scaleX,
        y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height,
      );

      // As Base64 string
      // const base64Image = canvas.toDataURL('image/jpeg');

      // As a blob
      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            }
          },
          'image/jpeg',
          1,
        );
      });
    }
  };

  return (
    <CModal
      isOpen={isOpen}
      onClose={(): void => {
        setImageData(undefined), onClose();
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton onClick={(): void => setImageData(undefined)} />
        <CModalBody>
          <CModalHeader></CModalHeader>

          <ReactCrop
            minHeight={100}
            src={fileUrl}
            circularCrop
            crop={crop}
            onImageLoaded={(image): void => {
              imageRef.current = image;
            }}
            onChange={(crop): void => {
              // We need to update the crop object only if the crop is within the image
              // This is to prevent the crop from going outside the image
              // This is a bug in react-image-crop
              if (!((crop?.y || 0) + (crop?.height || 0) > (imageRef?.current?.height || 0))) {
                setCrop(crop);
              } else {
                setCrop((prevState) => {
                  return { ...crop, y: prevState.y };
                });
              }
            }}
          />
        </CModalBody>
        <CModalFooter justifyContent="space-around">
          <ModalButton
            buttonText="Cancel"
            onClickHandler={(): void => {
              setImageData(undefined);
              onClose();
            }}
          />

          <ModalButton
            buttonText="Save"
            onClickHandler={async (): Promise<void> => {
              const image = imageRef.current;
              if (image) {
                const newBlob = await getCroppedImg(image, crop);

                onApplyCrop(newBlob);
                onClose();
              }
            }}
          />
        </CModalFooter>
      </ModalContent>
    </CModal>
  );
};

export default CropImageModal;
